import { BaseApiClient } from './base-client'
import { API_ROUTES, interpolateRoute } from '../api-routes.js'
import { Agent, AgentVoiceSettings, ApiResponse } from '@super/shared-types'
import { withErrorHandling } from '../utils'

export interface CreateAgentRequest {
  name: string
  companyId?: string
  voiceSettings: Omit<
    AgentVoiceSettings,
    'id' | 'agentId' | 'createdAt' | 'updatedAt'
  >
}

export interface UpdateAgentRequest {
  name?: string
  voiceSettings?: Partial<
    Omit<AgentVoiceSettings, 'id' | 'agentId' | 'createdAt' | 'updatedAt'>
  >
}

export interface AgentWithVoiceSettings extends Agent {
  voiceSettings?: AgentVoiceSettings
}

export class AgentsApiClient extends BaseApiClient {
  /**
   * Get company agent (for current user's company)
   * @returns The agent with voice settings
   */
  async getCompanyAgent(): Promise<AgentWithVoiceSettings | null> {
    return withErrorHandling(
      async () => {
        const agent = await this.request<ApiResponse<AgentWithVoiceSettings>>(
          API_ROUTES.AGENTS.GET,
        )
        return agent.data || null
      },
      {
        on404: 'ignore',
        errorContext: 'Error fetching company agent',
      },
    )
  }

  /**
   * Get agent for any company (SuperAdmin only)
   * @param companyId - The ID of the company to get the agent for
   * @returns The agent with voice settings
   */
  async getAgentForCompany(
    companyId: string,
  ): Promise<AgentWithVoiceSettings | null> {
    return withErrorHandling(
      async () => {
        const queryParams = { companyId }
        const agent = await this.request<ApiResponse<AgentWithVoiceSettings>>(
          `${API_ROUTES.ADMIN.AGENTS.GET}${this.buildQueryString(queryParams)}`,
        )
        return agent.data || null
      },
      {
        on404: 'ignore',
        errorContext: 'Error fetching agent for company',
      },
    )
  }

  /**
   * Create agent (for current user's company)
   * @param data - The data to create the agent with
   * @returns The created agent with voice settings
   */
  async createAgent(
    data: CreateAgentRequest,
  ): Promise<AgentWithVoiceSettings | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<AgentWithVoiceSettings>
        >(API_ROUTES.AGENTS.CREATE, {
          method: 'POST',
          body: JSON.stringify(data),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating agent',
      },
    )
  }

  /**
   * Update agent (for current user's company)
   * @param id - The ID of the agent to update
   * @param data - The data to update the agent with
   * @returns The updated agent with voice settings
   */
  async updateAgent(
    id: string,
    data: UpdateAgentRequest,
  ): Promise<AgentWithVoiceSettings | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<AgentWithVoiceSettings>
        >(interpolateRoute(API_ROUTES.AGENTS.UPDATE, { id }), {
          method: 'PUT',
          body: JSON.stringify(data),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating agent',
      },
    )
  }

  /**
   * Create agent for any company (SuperAdmin only)
   * @param data - The data to create the agent with
   * @returns The created agent with voice settings
   */
  async createAgentForCompany(
    data: CreateAgentRequest & { companyId: string },
  ): Promise<AgentWithVoiceSettings | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<AgentWithVoiceSettings>
        >(API_ROUTES.ADMIN.AGENTS.CREATE, {
          method: 'POST',
          body: JSON.stringify(data),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating agent for company',
      },
    )
  }

  /**
   * Update agent for any company (SuperAdmin only)
   * @param id - The ID of the agent to update
   * @param data - The data to update the agent with
   * @returns The updated agent with voice settings
   */
  async updateAgentForCompany(
    id: string,
    data: UpdateAgentRequest,
  ): Promise<AgentWithVoiceSettings | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<AgentWithVoiceSettings>
        >(interpolateRoute(API_ROUTES.ADMIN.AGENTS.UPDATE, { id }), {
          method: 'PUT',
          body: JSON.stringify(data),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating agent for company',
      },
    )
  }
}

export const agentsApiClient = new AgentsApiClient()
